import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { backupConfigAPI } from '../api/backupConfig';
import { 
  getAllScheduleMasters, 
  getAllSftpServerMasters 
} from '../services/api';
import { toast } from 'react-toastify';
import './CreateBackupConfigTabs.css';

const EditBackupConfig = () => {
  const navigate = useNavigate();
  const { configId } = useParams();
  
  const [activeTab, setActiveTab] = useState('basic');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [sftpServers, setSftpServers] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    configName: '',
    description: '',
    sqlServerConfig: {
      host: '',
      port: 1433,
      username: '',
      password: '',
      database: '',
      instanceName: ''
    },
    backupPath: '/backups',
    schedules: [],
    sftpServers: [],
    isActive: true
  });

  useEffect(() => {
    fetchData();
  }, [configId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [configRes, schedulesRes, serversRes] = await Promise.all([
        backupConfigAPI.getAll(1, 1000), // Get all configs to find this one
        getAllScheduleMasters(),
        getAllSftpServerMasters()
      ]);
      
      // Find the specific config
      const config = configRes.data.find(c => c._id === configId);
      
      if (!config) {
        toast.error('Configuration not found');
        navigate('/backup-configs');
        return;
      }

      setFormData({
        configName: config.configName || '',
        description: config.description || '',
        sqlServerConfig: {
          host: config.sqlServerConfig?.host || '',
          port: config.sqlServerConfig?.port || 1433,
          username: config.sqlServerConfig?.username || '',
          password: '', // Don't populate password for security
          database: config.sqlServerConfig?.database || '',
          instanceName: config.sqlServerConfig?.instanceName || ''
        },
        backupPath: config.backupPath || '/backups',
        schedules: config.schedules?.map(s => s._id || s) || [],
        sftpServers: config.sftpServers?.map(s => s._id || s) || [],
        isActive: config.isActive !== undefined ? config.isActive : true
      });

      setSchedules(schedulesRes.data.filter(s => s.isActive));
      setSftpServers(serversRes.data.filter(s => s.isActive));
    } catch (error) {
      toast.error('Failed to fetch configuration');
      navigate('/backup-configs');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e, section, field) => {
    const { value, type, checked } = e.target;
    
    if (section) {
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: type === 'number' ? Number(value) : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleScheduleToggle = (scheduleId) => {
    setFormData(prev => {
      const schedules = prev.schedules.includes(scheduleId)
        ? prev.schedules.filter(id => id !== scheduleId)
        : [...prev.schedules, scheduleId];
      return { ...prev, schedules };
    });
  };

  const handleSftpToggle = (serverId) => {
    setFormData(prev => {
      const sftpServers = prev.sftpServers.includes(serverId)
        ? prev.sftpServers.filter(id => id !== serverId)
        : [...prev.sftpServers, serverId];
      return { ...prev, sftpServers };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.schedules.length === 0) {
      toast.error('Please select at least one schedule');
      return;
    }
    
    if (formData.sftpServers.length === 0) {
      toast.error('Please select at least one SFTP server');
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Remove password if empty (don't update it)
      const submitData = { ...formData };
      if (!submitData.sqlServerConfig.password) {
        delete submitData.sqlServerConfig.password;
      }
      
      await backupConfigAPI.update(configId, submitData);
      toast.success('Backup configuration updated successfully');
      navigate('/backup-configs');
    } catch (error) {
      toast.error(error.message || 'Failed to update backup configuration');
    } finally {
      setSubmitting(false);
    }
  };

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: '📝' },
    { id: 'sql', label: 'SQL Server', icon: '🗄️' },
    { id: 'schedule', label: 'Schedule', icon: '⏰' },
    { id: 'sftp', label: 'SFTP Servers', icon: '🖥️' }
  ];

  const isTabValid = (tabId) => {
    switch (tabId) {
      case 'basic':
        return formData.configName.length >= 3;
      case 'sql':
        return formData.sqlServerConfig.host && 
               formData.sqlServerConfig.username && 
               formData.sqlServerConfig.database;
      case 'schedule':
        return formData.schedules.length > 0;
      case 'sftp':
        return formData.sftpServers.length > 0;
      default:
        return false;
    }
  };

  const canProceedToNext = () => {
    return isTabValid(activeTab);
  };

  const nextTab = () => {
    const currentIndex = tabs.findIndex(t => t.id === activeTab);
    if (currentIndex < tabs.length - 1 && canProceedToNext()) {
      setActiveTab(tabs[currentIndex + 1].id);
    }
  };

  const prevTab = () => {
    const currentIndex = tabs.findIndex(t => t.id === activeTab);
    if (currentIndex > 0) {
      setActiveTab(tabs[currentIndex - 1].id);
    }
  };

  if (loading) {
    return (
      <div className="config-form-tabs-container">
        <div className="loading">Loading configuration...</div>
      </div>
    );
  }

  return (
    <div className="config-form-tabs-container">
      <div className="form-header">
        <h1>Edit Backup Configuration</h1>
        <p>Update backup configuration settings</p>
      </div>

      <div className="tabs-navigation">
        {tabs.map((tab, index) => (
          <button
            key={tab.id}
            type="button"
            className={`tab-button ${activeTab === tab.id ? 'active' : ''} ${isTabValid(tab.id) ? 'valid' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
            {isTabValid(tab.id) && <span className="tab-check">✓</span>}
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit} className="config-tabs-form">
        <div className="tab-content">
          {/* Basic Info Tab */}
          {activeTab === 'basic' && (
            <div className="tab-pane">
              <h3>Basic Information</h3>
              
              <div className="form-group">
                <label htmlFor="configName">Configuration Name *</label>
                <input
                  type="text"
                  id="configName"
                  name="configName"
                  value={formData.configName}
                  onChange={(e) => handleChange(e, null, 'configName')}
                  required
                  placeholder="e.g., Production DB Backup"
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={(e) => handleChange(e, null, 'description')}
                  rows="3"
                  placeholder="Optional description"
                />
              </div>

              <div className="form-group">
                <label htmlFor="backupPath">Backup Path on SFTP Server</label>
                <input
                  type="text"
                  id="backupPath"
                  name="backupPath"
                  value={formData.backupPath}
                  onChange={(e) => handleChange(e, null, 'backupPath')}
                  placeholder="/backups or /path/to/backups"
                />
                <small className="help-text">
                  The directory path on the SFTP server where backups will be stored
                </small>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={(e) => handleChange(e, null, 'isActive')}
                  />
                  Active
                </label>
              </div>
            </div>
          )}

          {/* SQL Server Tab */}
          {activeTab === 'sql' && (
            <div className="tab-pane">
              <h3>SQL Server Configuration</h3>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="host">Host *</label>
                  <input
                    type="text"
                    id="host"
                    value={formData.sqlServerConfig.host}
                    onChange={(e) => handleChange(e, 'sqlServerConfig', 'host')}
                    required
                    placeholder="localhost or IP address"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="port">Port *</label>
                  <input
                    type="number"
                    id="port"
                    value={formData.sqlServerConfig.port}
                    onChange={(e) => handleChange(e, 'sqlServerConfig', 'port')}
                    required
                    min="1"
                    max="65535"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="username">Username *</label>
                  <input
                    type="text"
                    id="username"
                    value={formData.sqlServerConfig.username}
                    onChange={(e) => handleChange(e, 'sqlServerConfig', 'username')}
                    required
                    placeholder="SQL Server username"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <div className="password-input">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      value={formData.sqlServerConfig.password}
                      onChange={(e) => handleChange(e, 'sqlServerConfig', 'password')}
                      placeholder="Leave empty to keep current password"
                    />
                    <button
                      type="button"
                      className="toggle-password"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                  <small className="help-text">
                    Leave empty to keep the current password
                  </small>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="database">Database *</label>
                  <input
                    type="text"
                    id="database"
                    value={formData.sqlServerConfig.database}
                    onChange={(e) => handleChange(e, 'sqlServerConfig', 'database')}
                    required
                    placeholder="Database name"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="instanceName">Instance Name</label>
                  <input
                    type="text"
                    id="instanceName"
                    value={formData.sqlServerConfig.instanceName}
                    onChange={(e) => handleChange(e, 'sqlServerConfig', 'instanceName')}
                    placeholder="Optional instance name"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Schedule Tab */}
          {activeTab === 'schedule' && (
            <div className="tab-pane">
              <h3>Backup Schedule</h3>
              
              {schedules.length === 0 ? (
                <div className="empty-message">
                  <p>No schedules available. Please create a schedule first.</p>
                  <button
                    type="button"
                    onClick={() => window.open('/schedules/new', '_blank')}
                    className="btn btn-primary"
                  >
                    Create Schedule
                  </button>
                </div>
              ) : (
                <div className="selection-grid">
                  {schedules.map(schedule => (
                    <div
                      key={schedule._id}
                      className={`selection-card ${formData.schedules.includes(schedule._id) ? 'selected' : ''}`}
                      onClick={() => handleScheduleToggle(schedule._id)}
                    >
                      <input
                        type="checkbox"
                        checked={formData.schedules.includes(schedule._id)}
                        onChange={() => {}}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="card-content">
                        <h4>{schedule.name}</h4>
                        <p className="frequency">{schedule.frequency} at {schedule.time}</p>
                        {schedule.description && <p className="description">{schedule.description}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* SFTP Tab */}
          {activeTab === 'sftp' && (
            <div className="tab-pane">
              <h3>SFTP Servers</h3>
              
              {sftpServers.length === 0 ? (
                <div className="empty-message">
                  <p>No SFTP servers available. Please create an SFTP server first.</p>
                  <button
                    type="button"
                    onClick={() => window.open('/sftp-servers/new', '_blank')}
                    className="btn btn-primary"
                  >
                    Create SFTP Server
                  </button>
                </div>
              ) : (
                <div className="selection-grid">
                  {sftpServers.map(server => (
                    <div
                      key={server._id}
                      className={`selection-card ${formData.sftpServers.includes(server._id) ? 'selected' : ''}`}
                      onClick={() => handleSftpToggle(server._id)}
                    >
                      <input
                        type="checkbox"
                        checked={formData.sftpServers.includes(server._id)}
                        onChange={() => {}}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="card-content">
                        <h4>{server.name}</h4>
                        <p className="host">{server.host}:{server.port}</p>
                        {server.description && <p className="description">{server.description}</p>}
                        <p className="priority">Priority: {server.priority}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="tab-actions">
          <div className="left-actions">
            <button
              type="button"
              onClick={() => navigate('/backup-configs')}
              className="btn btn-secondary"
              disabled={submitting}
            >
              Cancel
            </button>
          </div>
          
          <div className="right-actions">
            {activeTab !== 'basic' && (
              <button
                type="button"
                onClick={prevTab}
                className="btn btn-secondary"
                disabled={submitting}
              >
                ← Previous
              </button>
            )}
            
            {activeTab !== 'sftp' ? (
              <button
                type="button"
                onClick={nextTab}
                className="btn btn-primary"
                disabled={!canProceedToNext() || submitting}
              >
                Next →
              </button>
            ) : (
              <button
                type="submit"
                className="btn btn-success"
                disabled={submitting || !isTabValid('sftp')}
              >
                {submitting ? 'Updating...' : 'Update Configuration'}
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default EditBackupConfig;
